import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { createNotificationClickHandler } from '../utils/notificationUtils';
import log from '../utils/logger';

const NotificationDropdown = ({ notifications, updateNotifications, limit = 4, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth0();

  const handleNotificationClick = createNotificationClickHandler(navigate, (updateFn) => {
    // Save to localStorage synchronously before state update
    if (user?.sub) {
      const storageKey = `notifications_${user.sub}`;
      try {
        const current = JSON.parse(localStorage.getItem(storageKey) || '[]');
        const updated = updateFn(current);
        localStorage.setItem(storageKey, JSON.stringify(updated));
      } catch (error) {
        log.error('Error updating localStorage:', error);
      }
    }

    // Update state
    updateNotifications(updateFn);

    // Close dropdown after click
    if (onClose) {
      onClose();
    }
  });

  if (!notifications || notifications.length === 0) {
    return (
      <div className="p-4 text-center text-gray-400">
        No new notifications
      </div>
    );
  }

  return (
    <>
      {notifications
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, limit)
        .map((notification) => (
          <div 
            key={notification.id} 
            className={`p-4 border-b border-dark-border hover:bg-dark-input transition-colors ${
              notification.action ? 'cursor-pointer' : ''
            }`}
            onClick={() => handleNotificationClick(notification)}
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className={`w-2 h-2 rounded-full mt-2 ${
                  notification.isRead ? 'bg-gray-600' : 'bg-snipehouse-blue'
                }`}></div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-300">{notification.content || notification.message}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {new Date(notification.createdAt).toLocaleString(undefined, { 
                    month: 'short', 
                    day: 'numeric', 
                    hour: 'numeric', 
                    minute: '2-digit', 
                    hour12: true 
                  })}
                </p>
                {notification.action && (
                  <p className="text-xs text-snipehouse-blue mt-1">
                    Click to {notification.action === 'payment' ? 'proceed to payment' : 
                             notification.action === 'auction' ? 'view auction' : 
                             notification.action === 'order' ? 'view order details' : 
                             `go to ${notification.action}`}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
    </>
  );
};

NotificationDropdown.propTypes = {
  notifications: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    content: PropTypes.string,
    createdAt: PropTypes.string,
    isRead: PropTypes.bool,
    action: PropTypes.string,
  })).isRequired,
  updateNotifications: PropTypes.func.isRequired,
  limit: PropTypes.number,
  onClose: PropTypes.func,
};

export default NotificationDropdown;
